/** Super-admin: one tenant — plan/seats (with downgrade preview) + modules. */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError } from "../../api/http";
import * as api from "../../api/tenants";
import { TIERS, type ModuleStatus, type Tenant } from "../../api/types";
import { useToast } from "../../components/ToastContext";
import { Badge, Button, Card, Label, Select, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { TenantCredits } from "./TenantCredits";

export function TenantDetailPage() {
  const t = useT();
  const { push } = useToast();
  const { tenantId = "" } = useParams();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [modules, setModules] = useState<ModuleStatus[]>([]);
  const [tier, setTier] = useState("");
  const [preview, setPreview] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const [tenantRow, moduleRows] = await Promise.all([
      api.getTenant(tenantId),
      api.listTenantModules(tenantId),
    ]);
    setTenant(tenantRow);
    setTier(tenantRow.subscription_tier);
    setModules(moduleRows);
  }, [tenantId]);

  useEffect(() => {
    reload().catch(() => push("error", t("common.error.generic")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const onTierChange = async (nextTier: string) => {
    setTier(nextTier);
    setPreview(null);
    if (tenant && nextTier !== tenant.subscription_tier) {
      // Read-only what-if BEFORE saving — the confirm-dialog discipline.
      const p = await api.previewSeats(tenantId, { subscription_tier: nextTier });
      setPreview(p.would_deactivate);
    }
  };

  const onSave = async () => {
    setBusy(true);
    try {
      await api.updateTenant(tenantId, { subscription_tier: tier });
      push("success", t("common.save"));
      setPreview(null);
      await reload();
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  const onToggleModule = async (m: ModuleStatus) => {
    try {
      if (m.enabled) {
        await api.disableModule(tenantId, m.code, true);
      } else {
        await api.enableModule(tenantId, m.code);
      }
      setModules(await api.listTenantModules(tenantId));
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    }
  };

  if (!tenant) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold">{tenant.legal_name}</h1>
        <p className="mt-1 text-muted">
          {tenant.subdomain} · <Badge tone="brand">{tenant.subscription_tier}</Badge>
        </p>
      </div>

      <Card title={t("tenants.tier")}>
        <div className="flex items-end gap-4">
          <div className="w-48">
            <Label htmlFor="tier">{t("tenants.tier")}</Label>
            <Select id="tier" value={tier} onChange={(e) => onTierChange(e.target.value)}>
              {TIERS.map((tierOption) => (
                <option key={tierOption} value={tierOption}>
                  {tierOption}
                </option>
              ))}
            </Select>
          </div>
          <Button onClick={onSave} loading={busy} disabled={tier === tenant.subscription_tier}>
            {t("tenants.save")}
          </Button>
        </div>
        {preview !== null && preview > 0 && (
          <p role="alert" className="mt-3 text-sm font-medium text-warning">
            {t("tenants.seatPreview", { n: preview })}
          </p>
        )}
      </Card>

      <TenantCredits tenantId={tenantId} />

      <Card title={t("tenants.modules")}>
        <ul className="divide-y divide-line/60">
          {modules.map((m) => (
            <li key={m.code} className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-semibold text-ink">
                  {m.name} <span className="font-normal text-muted">({m.code})</span>
                </p>
                <p className="text-xs text-muted">{m.description}</p>
              </div>
              {m.always_on ? (
                <Badge tone="neutral">always on</Badge>
              ) : (
                <Button
                  variant={m.enabled ? "secondary" : "primary"}
                  onClick={() => onToggleModule(m)}
                  aria-label={`${m.enabled ? "disable" : "enable"} ${m.code}`}
                >
                  {m.enabled ? "Disable" : "Enable"}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
